import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { ConfirmationModal } from '@/components/core/confirmation-modal/confirmation-modal';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { createBatchTableColumns } from '../../components/batch-table/batch-table-columns';
import { SendSurveyDialog } from '../../components/send-survey-dialog/send-survey-dialog';
import { DEFAULT_SURVEY_BATCHES_ORDER } from '../../graphql/queries';
import { useDeleteSurveyBatch, useGetSurveyBatches } from '../../hooks/use-surveys';
import { SurveyBatch, SurveyBatchesPagingResult } from '../../types/survey.types';
import { PageHeader } from '@/components/core';

export const SurveyListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_SURVEY_BATCHES_ORDER,
  });
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<SurveyBatch | null>(null);

  const { data, isLoading, error } = useGetSurveyBatches({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere({}, columnWhere),
    order,
  });
  const result = (data as { SurveyBatches?: SurveyBatchesPagingResult })?.SurveyBatches;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const { mutateAsync: deleteSurveyBatch, isPending: isDeleting } = useDeleteSurveyBatch();

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    await deleteSurveyBatch(deleteTarget.ItemId);
    setDeleteTarget(null);
  };

  const columns = createBatchTableColumns({ t, onDelete: setDeleteTarget });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('SURVEYS')}
        actions={[
          {
            label: t('SEND_SURVEY'),
            icon: <Plus className="size-5" />,
            onClick: () => setIsSendOpen(true),
          },
        ]}
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        onRowClick={(batch: SurveyBatch) => navigate(`/hr/surveys/${batch.ItemId}`)}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <SendSurveyDialog open={isSendOpen} onOpenChange={setIsSendOpen} />

      <ConfirmationModal
        open={Boolean(deleteTarget)}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={t('DELETE_SURVEY')}
        description={t('ARE_YOU_SURE_WANT_DELETE')}
        confirmText={isDeleting ? t('DELETING') : t('DELETE')}
        preventAutoClose={isDeleting}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};
